// AccessView.jsx — View PURA da /access (Gestão de Acessos). View/ViewModel à risca.
// Recebe { vm, actions } — sem fetch, sem validação de e-mail, sem regra de permissão.
// Contrato: access.vm.js (@version 3). Situações cobertas: loading, error, empty, ready,
// form aberto (pessoa encontrada / e-mail sem conta / e-mail inválido), linha editando, linha removendo,
// quem recebeu saindo do compartilhamento, dois profissionais com o mesmo nome (agrupamento por
// professionalId, nunca pelo nome; a especialidade diz qual é qual).
//
// v3 — remover e sair: a ação aparece quando a linha traz `revoke` (não depende mais de canEdit) e TODOS os
// textos vêm dele — botão, pergunta, explicação, os dois botões e o "…" enquanto `revoking`. Na linha enviada
// é um ícone ao lado da pessoa (como editar); no cartão recebido, um botão com o texto no rodapé, porque ali é
// a única ação. A confirmação é a mesma nos dois lados (mode 'revoking') e o foco entra nela ao abrir.
// Especialidade (`professionalSpecialty` / `specialty`): o 2º rótulo, no cabeçalho do grupo, no cartão
// recebido e no seletor do formulário. Vem null na maioria das contas, então nada depende dela.
//
// Rodada 4 — a confirmação abre INTEIRA (foco sem rolar + scrollIntoView 'nearest': se já cabe, nada se mexe;
// se não cabe, rola só até ela encostar no pé; suave só sem prefers-reduced-motion) e o foco nunca cai na
// página. Tudo lido do vm, como o container entrega:
//   · esperando o servidor (`revoking` liga, os botões desligam) → o foco fica na caixa;
//   · cancelou (a linha continua, `mode` volta a 'idle') → o foco volta para o botão que abriu;
//   · deu certo (a linha que estava em 'revoking' some de `rows`) → o foco vai para o título da faixa;
//   · falhou (continua 'revoking', `revoking` volta a false) → o foco volta para a caixa.
// Só se mexe no foco quando ele estava na confirmação ou caiu na página — nunca tira a pessoa de outro lugar.
// No primeiro desenho da tela (no estúdio, o mock já nasce com a caixa aberta) nada rola nem recebe foco.
//
// Rodada 5 — o aviso de erro do container (Snackbar no pé: 56 px de baixo no celular, 72 px a partir de 600 px)
// nunca cobre os botões da confirmação. A folga vem já na abertura (FOLGA_AVISO, 80 px): a caixa tem
// scroll-margin-bottom de 80 px, então o mesmo scrollIntoView 'nearest' a deixa terminar 80 px acima do pé —
// se ela já termina acima disso, nada se mexe. Quando o erro aparece, nada rola. E, enquanto há uma
// confirmação aberta, a tela ganha 80 px no fim, para o último cartão também ter para onde subir.
//
// Ideia da tela — "quem vê o quê", em duas faixas:
//   · VOCÊ COMPARTILHOU: os seus profissionais, AGRUPADOS POR professionalId. Cada grupo responde
//     "quem vê a agenda da Ana?" — a lista de pessoas com acesso a ela, cada uma com o que pode fazer.
//   · COMPARTILHARAM COM VOCÊ: os profissionais de outras contas que você acessa, com o dono.
//   As faixas ficam lado a lado quando há largura (container query: o pane do Admin é a janela menos o
//   menu, então quem decide é a largura real do conteúdo, não a da janela) e empilham no celular.
//   Nada de tabela: em qualquer largura são cartões, e a edição/remoção acontece no próprio cartão.
//
// Permissões: na leitura, um MEDIDOR por área a partir de `permissionLevels` (v2) — as 4 áreas sempre,
// inclusive o que a pessoa NÃO vê; cada área diz o nível por escrito + ícone (bloqueio / olho / lápis) e
// duas barrinhas que enchem (estado nunca só por cor). Na escrita (form e linha editando), a matriz
// 4 áreas × 3 níveis (Nenhum / Ver / Editar) com ToggleButtons — as mesmas palavras nos dois lugares.
//
// Profissional: foto (photoUrl) por cima das iniciais; se a foto falhar (onError) fica só a inicial.
// Estado só de UI: a foto quebrou (useState, por avatar) e o foco/rolagem da confirmação (useEffect + useRef).
//
// Consistência com Serviços/Ausências: mesmo CARD_SX, mesmo hover, mesmos chips de tint, mesmo
// StateBlock, mesmo par de IconButton 40×40, mesmo botão primário do header.
// Neutros vêm de --bg-page/--divider/--text-* (papéis themeáveis).
// Regras: estilo só via `sx`; cores/medidas via var(--*); MUI v6 + @mui/icons-material; tipografia
// via <Typography variant>; o resto do que muda vem do VM.
import { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, IconButton, Tooltip, TextField, Select, MenuItem,
  CircularProgress, Skeleton, ToggleButton, ToggleButtonGroup, InputAdornment,
} from '@mui/material';
import ManageAccountsRounded from '@mui/icons-material/ManageAccountsRounded';
import PersonAddAltRounded from '@mui/icons-material/PersonAddAltRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CallMadeRounded from '@mui/icons-material/CallMadeRounded';
import CallReceivedRounded from '@mui/icons-material/CallReceivedRounded';
import BadgeRounded from '@mui/icons-material/BadgeRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import BlockRounded from '@mui/icons-material/BlockRounded';
import PersonRemoveRounded from '@mui/icons-material/PersonRemoveRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import HandshakeRounded from '@mui/icons-material/HandshakeRounded';
import EventNoteRounded from '@mui/icons-material/EventNoteRounded';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import PersonSearchRounded from '@mui/icons-material/PersonSearchRounded';
import HowToRegRounded from '@mui/icons-material/HowToRegRounded';
import ForwardToInboxRounded from '@mui/icons-material/ForwardToInboxRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import MedicalServicesRounded from '@mui/icons-material/MedicalServicesRounded';

// TÍTULO DE PÁGINA: <Typography variant="h1"> puro — a escala vive no tema pinado.

const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};

const HOVER_SX = {
  transition: 'box-shadow .15s, border-color .15s',
  '&:hover': { boxShadow: 'var(--shadow-hover)', borderColor: 'var(--color-primary-light)' },
};

// Anel de foco visível para tudo que é clicável (o tema já cuida dos campos).
const FOCUS_SX = {
  '&.Mui-focusVisible, &:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' },
};

const ICON_BTN_SX = {
  ...FOCUS_SX,
  width: 40, height: 40, color: 'var(--text-secondary)',
  '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
};
const REMOVE_BTN_SX = {
  ...FOCUS_SX,
  width: 40, height: 40, color: 'var(--text-secondary)',
  '&:hover': { color: 'var(--color-error)', bgcolor: 'var(--tint-error-bg)' },
};

const PRIMARY_BTN_SX = {
  ...FOCUS_SX,
  bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.25, minHeight: 42,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-dark)' },
  '&.Mui-disabled': { bgcolor: 'var(--divider)', color: 'var(--text-disabled)' },
};
const OUTLINE_BTN_SX = {
  ...FOCUS_SX,
  minHeight: 42, px: 2.25, borderRadius: 'var(--radius-sm)',
  border: '1px solid', borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)',
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-soft)' },
  '&.Mui-disabled': { borderColor: 'var(--divider)', color: 'var(--text-disabled)' },
};
const QUIET_BTN_SX = {
  ...FOCUS_SX,
  minHeight: 42, px: 2, borderRadius: 'var(--radius-sm)',
  border: '1px solid', borderColor: 'var(--divider)', color: 'var(--text-secondary)',
  fontWeight: 'var(--fw-medium)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&.Mui-disabled': { borderColor: 'var(--divider)', color: 'var(--text-disabled)' },
};
// "Sair do compartilhamento": quieto como o QUIET, e no hover avisa no tom de remover (como o REMOVE_BTN).
const LEAVE_BTN_SX = {
  ...QUIET_BTN_SX,
  '&:hover': { borderColor: 'var(--tint-error-fg)', color: 'var(--tint-error-fg)', bgcolor: 'var(--tint-error-bg)' },
};
// Destrutivo "sem susto": nada de vermelho chapado; pastilha de tint com texto forte.
const DANGER_BTN_SX = {
  ...FOCUS_SX,
  minHeight: 42, px: 2, borderRadius: 'var(--radius-sm)',
  bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--tint-error-fg)', color: 'var(--tint-error-fg)',
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--tint-error-border)' },
  '&.Mui-disabled': { borderColor: 'var(--tint-error-border)', color: 'var(--tint-error-sub)' },
};

const CONTAINER_SX = { containerType: 'inline-size' };

// Nome do profissional para leitor de tela: com a especialidade quando existe (duas "Bruna Lima" ≠).
const profLabel = (name, specialty) => (specialty ? `${name} (${specialty})` : name);

/* ── Foco e rolagem (só comportamento de UI) ──────────────────────────── */
const semMovimento = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return true; }
};
// O foco "caiu na página": ninguém focado, ou o elemento focado saiu da tela.
const focoPerdido = () => {
  const ae = typeof document !== 'undefined' ? document.activeElement : null;
  return !ae || ae === document.body || ae === document.documentElement || ae.isConnected === false;
};
// Leva o foco sem rolar e, se for para mostrar, rola só o necessário (block 'nearest' — nunca pula para o topo).
//   'sempre'  → a confirmação abrindo: tem que aparecer inteira, no toque e no teclado;
//   'teclado' → só quando o navegador marca o foco como visível (quem navega pelo teclado); no toque/mouse a
//               tela não se mexe;
//   'nunca'   → o foco muda, a tela fica.
function levarFoco(el, mostrar = 'nunca') {
  if (!el || typeof el.focus !== 'function') return;
  el.focus({ preventScroll: true });
  let rolar = mostrar === 'sempre';
  if (mostrar === 'teclado') {
    try { rolar = el.matches(':focus-visible'); } catch { rolar = false; }
  }
  if (rolar && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: semMovimento() ? 'auto' : 'smooth' });
  }
}
// Folga para o aviso de erro do container (Snackbar): ele ocupa os 56 px de baixo no celular e os 72 px de
// baixo a partir de 600 px. 80 px = 2 × --space-4 + --space-2. Vale como scroll-margin da confirmação (onde
// ela pode terminar) e como espaço extra no fim da tela enquanto ela está aberta (para ter para onde rolar).
const FOLGA_AVISO = 'calc(var(--space-4) * 2 + var(--space-2))';
// Título que recebe foco por código (tabIndex -1): anel só quando o foco é de teclado.
const FOCUS_TITLE_SX = {
  borderRadius: 'var(--radius-sm)',
  '&:focus': { outline: 'none' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '3px' },
};
const SR_ONLY_SX = {
  position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
};

// Uma "bolinha" de ícone — avatar sem dado de foto (ver pedido de dado na entrega).
function IconDot({ icon, tone = 'neutral', size = 40 }) {
  const t = {
    neutral: { bg: 'var(--bg-page)', fg: 'var(--text-secondary)' },
    primary: { bg: 'var(--color-primary-soft)', fg: 'var(--color-primary-dark)' },
    info: { bg: 'var(--tint-info-bg)', fg: 'var(--tint-info-fg)' },
    success: { bg: 'var(--tint-success-bg)', fg: 'var(--tint-success-fg)' },
    error: { bg: 'var(--tint-error-bg)', fg: 'var(--tint-error-fg)' },
  }[tone];
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        bgcolor: t.bg, color: t.fg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {icon}
    </Box>
  );
}

/* ── Avatar do profissional: foto por cima das iniciais ───────────────── */
// A inicial está sempre desenhada embaixo; a <img> cobre quando carrega e some no onError.
// Assim não há "piscar" de texto alternativo nem caixa quebrada — e sem foto a tela continua inteira.
function ProfAvatar({ photoUrl, initials, tone = 'primary', size = 44 }) {
  const [broken, setBroken] = useState(false);
  const t = tone === 'info'
    ? { bg: 'var(--tint-info-bg)', fg: 'var(--tint-info-fg)' }
    : { bg: 'var(--color-primary-soft)', fg: 'var(--color-primary-dark)' };
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'relative', width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
        bgcolor: t.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Typography
        component="span"
        variant={size >= 40 ? 'subtitle2' : 'caption'}
        sx={{ color: t.fg, fontWeight: 'var(--fw-bold)', letterSpacing: '.02em', lineHeight: 1 }}
      >
        {initials}
      </Typography>
      {!!photoUrl && !broken && (
        <Box
          component="img"
          src={photoUrl}
          alt=""
          loading="lazy"
          onError={() => setBroken(true)}
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      )}
    </Box>
  );
}

/* ── Direção (Enviado / Recebido): ícone + rótulo do VM ───────────────── */
function DirectionTag({ direction, label }) {
  const given = direction === 'given';
  const Icon = given ? CallMadeRounded : CallReceivedRounded;
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
        bgcolor: given ? 'var(--color-primary-soft)' : 'var(--tint-info-bg)',
        border: '1px solid', borderColor: given ? 'var(--color-primary-soft)' : 'var(--tint-info-border)',
        borderRadius: 999, px: 1, py: '2px',
      }}
    >
      <Icon sx={{ fontSize: 14, color: given ? 'var(--color-primary-dark)' : 'var(--tint-info-fg)' }} />
      <Typography variant="caption" sx={{ color: given ? 'var(--color-primary-dark)' : 'var(--tint-info-fg)', fontWeight: 'var(--fw-bold)' }}>
        {label}
      </Typography>
    </Box>
  );
}

/* ── Níveis (as mesmas palavras na leitura e na escrita) ──────────────── */
const LEVELS = [
  { v: 0, label: 'Nenhum', Icon: BlockRounded },
  { v: 1, label: 'Ver', Icon: VisibilityRounded },
  { v: 2, label: 'Editar', Icon: EditRounded },
];

/* ── Medidor de permissões (leitura): as 4 áreas, de permissionLevels ── */
// Cada área: ícone da área + nome, e o nível escrito com o ícone do nível e 2 barrinhas (0, 1 ou 2 cheias).
// "Nenhum" fica em traço tracejado e texto secundário — continua legível (≥ 4,5:1), só mais quieto.
const METER_TONE = {
  0: { bg: 'transparent', border: 'var(--divider)', style: 'dashed', fg: 'var(--text-secondary)', bar: 'var(--divider)' },
  1: { bg: 'var(--bg-page)', border: 'var(--divider)', style: 'solid', fg: 'var(--text-primary)', bar: 'var(--text-secondary)' },
  2: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', style: 'solid', fg: 'var(--tint-success-fg)', bar: 'var(--tint-success-fg)' },
};

function PermMeter({ entities, levels, label }) {
  const lv = levels || {};
  return (
    <Box sx={{ ...CONTAINER_SX, containerName: 'medidor' }}>
      <Box
        component="ul"
        aria-label={label}
        sx={{
          listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.75,
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          '@container medidor (min-width: 520px)': { gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' },
        }}
      >
        {(entities || []).map((ent) => {
          const level = lv[ent.key] ?? 0;
          const t = METER_TONE[level] || METER_TONE[0];
          const L = LEVELS[level] || LEVELS[0];
          const LevelIcon = L.Icon;
          const EntIcon = ENTITY_ICON[ent.key] || BadgeRounded;
          return (
            <Box
              component="li"
              key={ent.key}
              aria-label={`${ent.label}: ${L.label}`}
              sx={{
                minWidth: 0, bgcolor: t.bg, border: '1px', borderStyle: t.style, borderColor: t.border,
                borderRadius: 'var(--radius-sm)', px: 1.125, py: 0.875, display: 'flex', flexDirection: 'column', gap: 0.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <EntIcon aria-hidden="true" sx={{ fontSize: 14, color: 'var(--text-secondary)', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {ent.label}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <LevelIcon aria-hidden="true" sx={{ fontSize: 15, color: t.fg, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: t.fg, fontWeight: level ? 'var(--fw-bold)' : 'var(--fw-medium)', whiteSpace: 'nowrap' }}>
                    {L.label}
                  </Typography>
                </Box>
                <Box aria-hidden="true" sx={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
                  {[1, 2].map((i) => (
                    <Box key={i} sx={{ width: 10, height: 6, borderRadius: 999, bgcolor: level >= i ? t.bar : 'var(--divider)' }} />
                  ))}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/* ── Matriz de permissões (escrita): 4 áreas × Nenhum / Ver / Editar ──── */
const LEVEL_ON = {
  0: { bg: 'var(--tint-neutral-bg)', border: 'var(--text-secondary)', fg: 'var(--text-primary)' },
  1: { bg: 'var(--color-primary-soft)', border: 'var(--color-primary)', fg: 'var(--color-primary-dark)' },
  2: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-fg)', fg: 'var(--tint-success-fg)' },
};
const ENTITY_ICON = {
  professional: BadgeRounded,
  appointments: EventNoteRounded,
  schedules: CalendarMonthRounded,
  persons: PeopleAltRounded,
};

function PermMatrix({ entities, values, onChange, disabled, idPrefix }) {
  const v = values || {};
  return (
    <Box sx={{ ...CONTAINER_SX, containerName: 'matriz' }}>
      <Box
        component="ul"
        sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        {(entities || []).map((ent) => {
          const EntIcon = ENTITY_ICON[ent.key] || BadgeRounded;
          const labelId = `${idPrefix}-${ent.key}`;
          return (
            <Box
              component="li"
              key={ent.key}
              sx={{
                display: 'grid', gridTemplateColumns: '1fr', gap: 0.75, alignItems: 'center',
                '@container matriz (min-width: 420px)': { gridTemplateColumns: 'minmax(120px, 0.8fr) minmax(0, 2fr)', gap: 1.5 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <EntIcon aria-hidden="true" sx={{ fontSize: 18, color: 'var(--text-secondary)', flexShrink: 0 }} />
                <Typography id={labelId} variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>
                  {ent.label}
                </Typography>
              </Box>
              <ToggleButtonGroup
                exclusive
                value={v[ent.key] ?? 0}
                onChange={(e, level) => { if (level !== null && level !== undefined) onChange?.(ent.key, level); }}
                aria-labelledby={labelId}
                disabled={disabled}
                sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.75 }}
              >
                {LEVELS.map(({ v: lv, label, Icon }) => {
                  const on = LEVEL_ON[lv];
                  const off = lv === 0 && ent.noNone;
                  return (
                    <ToggleButton
                      key={lv}
                      value={lv}
                      disabled={off || disabled}
                      aria-label={`${ent.label}: ${label}${off ? ' (indisponível — mínimo Ver)' : ''}`}
                      sx={{
                        ...FOCUS_SX,
                        minHeight: 40, px: 1, gap: 0.625, textTransform: 'none',
                        border: '1px solid !important', borderColor: 'var(--divider) !important',
                        borderRadius: 'var(--radius-sm) !important', m: '0 !important',
                        bgcolor: 'var(--surface)', color: 'var(--text-secondary)',
                        fontWeight: 'var(--fw-medium)',
                        '&:hover': { bgcolor: 'var(--bg-page)', color: 'var(--text-primary)' },
                        '&.Mui-selected, &.Mui-selected:hover': {
                          bgcolor: on.bg, color: on.fg, borderColor: `${on.border} !important`,
                          fontWeight: 'var(--fw-bold)', boxShadow: `inset 0 0 0 1px ${on.border}`,
                        },
                        '&.Mui-disabled': {
                          color: 'var(--text-disabled)', borderStyle: 'dashed !important', bgcolor: 'transparent',
                        },
                      }}
                    >
                      <Icon aria-hidden="true" sx={{ fontSize: 17 }} />
                      <Typography component="span" variant="body2" sx={{ fontWeight: 'inherit', color: 'inherit', whiteSpace: 'nowrap' }}>
                        {label}
                      </Typography>
                      {(v[ent.key] ?? 0) === lv && (
                        <CheckRounded aria-hidden="true" sx={{ fontSize: 15, display: { xs: 'none', sm: 'inline-block' } }} />
                      )}
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function Hint({ children }) {
  if (!children) return null;
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1,
        bgcolor: 'var(--tint-info-bg)', border: '1px solid', borderColor: 'var(--tint-info-border)',
        borderRadius: 'var(--radius-sm)', px: 1.5, py: 1,
      }}
    >
      <InfoOutlined aria-hidden="true" sx={{ fontSize: 18, color: 'var(--tint-info-fg)', flexShrink: 0, mt: '1px' }} />
      <Typography variant="caption" sx={{ color: 'var(--tint-info-fg)', lineHeight: 1.45 }}>{children}</Typography>
    </Box>
  );
}

function Spinner({ label }) {
  return <CircularProgress size={16} aria-label={label} sx={{ color: 'inherit' }} />;
}

/* ── Datas: criado / editado ──────────────────────────────────────────── */
function Dates({ row, labels }) {
  const item = (label, value) => (
    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
      {label}{' '}
      <Box component="span" sx={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
    </Typography>
  );
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25 }}>
      {item(labels.createdAt, row.createdAtLabel)}
      {item(labels.updatedAt, row.updatedAtLabel)}
    </Box>
  );
}

/* ── Confirmação de remover / sair (mode 'revoking', nos dois lados) ──── */
// Todos os textos vêm de `row.revoke` — do lado de quem está na tela. Ao abrir, o foco entra na própria
// caixa e a página rola só o necessário para ela aparecer inteira: o leitor de tela lê a pergunta, e o
// próximo Tab cai no botão seguro (cancelar).
//   quiet     → a caixa já nasceu aberta no primeiro desenho da tela (o mock do estúdio): nada rola nem foca.
//   focusFlag → ref do item: "o foco estava aqui dentro" — é o que autoriza devolver o foco ao cancelar.
function RevokeConfirm({ row, a, received, quiet, focusFlag }) {
  const r = row.revoke;
  const boxRef = useRef(null);
  // Abriu: foco na caixa e rolagem mínima até ela caber inteira na tela, 80 px acima do pé (scroll-margin).
  // Vale o `quiet` da MONTAGEM (o primeiro desenho), guardado num ref — o efeito roda uma vez só.
  const quietNaMontagem = useRef(quiet);
  useEffect(() => {
    if (!quietNaMontagem.current) levarFoco(boxRef.current, 'sempre');
  }, []);
  // `revoking` mudou: ligou (esperando o servidor — os botões desligam e o navegador tiraria o foco deles)
  // ou desligou com a caixa ainda aberta (o servidor falhou). Nos dois casos o foco fica/volta na caixa.
  const revokingAntes = useRef(row.revoking);
  useEffect(() => {
    if (revokingAntes.current === row.revoking) return;
    revokingAntes.current = row.revoking;
    const box = boxRef.current;
    if (box && (focoPerdido() || box.contains(document.activeElement))) levarFoco(box, 'nunca');
  }, [row.revoking]);
  const Icon = received ? LogoutRounded : PersonRemoveRounded;
  const titleId = `rev-${row.key}`;
  const bodyId = `rev-${row.key}-texto`;
  const btn = { flex: { xs: '1 1 auto', sm: '0 0 auto' } };
  return (
    <Box
      ref={boxRef}
      tabIndex={-1}
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      aria-busy={row.revoking || undefined}
      onFocus={() => { if (focusFlag) focusFlag.current = true; }}
      onBlur={(e) => { if (focusFlag && !e.currentTarget.contains(e.relatedTarget)) focusFlag.current = false; }}
      sx={{
        bgcolor: 'var(--tint-error-bg)', border: '1px solid', borderColor: 'var(--tint-error-border)',
        borderRadius: 'var(--radius-sm)', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25,
        '&:focus': { outline: 'none' },
        // O scrollIntoView da abertura respeita esta margem: a caixa termina acima da faixa do aviso de erro.
        scrollMarginBottom: FOLGA_AVISO,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        <Icon aria-hidden="true" sx={{ fontSize: 22, color: 'var(--tint-error-fg)', flexShrink: 0, mt: '1px' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography id={titleId} variant="subtitle2" sx={{ color: 'var(--tint-error-fg)', overflowWrap: 'anywhere' }}>
            {r.title}
          </Typography>
          <Typography id={bodyId} variant="body2" sx={{ color: 'var(--text-primary)', mt: 0.5, lineHeight: 1.45 }}>
            {r.body}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button onClick={() => a.onCancelRevoke?.()} disabled={row.revoking} sx={{ ...QUIET_BTN_SX, ...btn }}>
          {r.cancelLabel}
        </Button>
        <Button
          onClick={() => a.onConfirmRevoke?.(row.key)}
          disabled={row.revoking}
          startIcon={row.revoking ? <Spinner label={r.progressLabel} /> : <Icon />}
          sx={{ ...DANGER_BTN_SX, ...btn }}
        >
          {row.revoking ? r.progressLabel : r.confirmLabel}
        </Button>
      </Box>
      {/* Botão desligado não é anunciado: o "Saindo…/Removendo…" vai também para o leitor de tela. */}
      <Box role="status" aria-live="polite" sx={SR_ONLY_SX}>{row.revoking ? r.progressLabel : ''}</Box>
    </Box>
  );
}

/* ── Um acesso (dentro do grupo do profissional ou cartão próprio) ────── */
// inGroup: o profissional já está no cabeçalho do grupo → o item destaca a PESSOA.
function AccessItem({ row, vm, a, inGroup, quiet }) {
  const labels = vm.columnLabels || {};
  // Cancelou (a linha continua e `mode` sai de 'revoking'): se o foco estava na confirmação, ele volta para o
  // botão que a abriu — o ícone na linha enviada, "Sair do compartilhamento" no cartão recebido.
  const triggerRef = useRef(null);
  const focoNaConfirmacao = useRef(false);
  const modoAntes = useRef(row.mode);
  useEffect(() => {
    const antes = modoAntes.current;
    modoAntes.current = row.mode;
    if (antes === 'revoking' && row.mode !== 'revoking') {
      if (focoNaConfirmacao.current && focoPerdido()) levarFoco(triggerRef.current, 'teclado');
      focoNaConfirmacao.current = false;
    }
  }, [row.mode]);
  const received = row.direction === 'received';
  const editing = row.mode === 'editing';
  const revoke = row.revoke || null;
  const revoking = row.mode === 'revoking' && !!revoke;
  const idle = row.mode !== 'editing' && row.mode !== 'revoking';
  const personName = row.sharedWithName;
  const prof = profLabel(row.professionalName, row.professionalSpecialty);
  const who = `${personName} — ${prof}`;

  const frame = editing
    ? { borderColor: 'var(--color-primary)', bgcolor: 'var(--surface)' }
    : revoking
      ? { borderColor: 'var(--tint-error-border)', bgcolor: 'var(--surface)' }
      : null;

  // Editar: só com canEdit. Remover/sair: só com `revoke` (v3 — independente de canEdit).
  // No grupo (linha enviada) os dois são ícones ao lado da pessoa; no cartão próprio (recebido), o
  // remover/sair vira botão com o texto no rodapé.
  const showEdit = row.canEdit && idle;
  const iconRevoke = inGroup && !!revoke && idle;
  const textRevoke = !inGroup && !!revoke && idle;

  const actions = showEdit || iconRevoke ? (
    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
      {showEdit && (
        <Tooltip title="Editar permissões">
          <IconButton aria-label={`Editar permissões de ${who}`} onClick={() => a.onStartEdit?.(row.key)} sx={ICON_BTN_SX}>
            <EditRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
      {iconRevoke && (
        <Tooltip title={revoke.actionLabel}>
          <IconButton ref={triggerRef} aria-label={`${revoke.actionLabel}: ${who}`} onClick={() => a.onAskRevoke?.(row.key)} sx={REMOVE_BTN_SX}>
            {received ? <LogoutRounded sx={{ fontSize: 20 }} /> : <PersonRemoveRounded sx={{ fontSize: 20 }} />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  ) : null;

  // Quem está do outro lado: com quem compartilhei (given) ou o dono (received).
  const person = received ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      <IconDot icon={<HandshakeRounded sx={{ fontSize: 18 }} />} tone="info" size={36} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
          {row.ownerNote || personName}
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
          {row.sharedWithEmail}
        </Typography>
      </Box>
    </Box>
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      <IconDot icon={<PersonRounded sx={{ fontSize: 20 }} />} size={36} />
      <Box sx={{ minWidth: 0 }}>
        {!inGroup && (
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{labels.sharedWith}</Typography>
        )}
        <Typography variant="subtitle2" sx={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
          {personName}
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
          {row.sharedWithEmail}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      component={inGroup ? 'li' : 'article'}
      aria-label={inGroup ? `${personName}: acesso a ${prof}` : `${prof} — ${row.ownerNote || personName}`}
      sx={{
        ...(inGroup
          ? {
            border: '1px solid', borderColor: 'transparent', borderRadius: 'var(--radius-sm)',
            p: 1.5, transition: 'background-color .15s, border-color .15s',
            '&:hover': editing || revoking ? {} : { bgcolor: 'var(--bg-page)' },
          }
          : { ...CARD_SX, ...(editing || revoking ? {} : HOVER_SX), p: { xs: 2, sm: 2.25 } }),
        ...(frame || {}),
        display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0,
      }}
    >
      {!inGroup && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <ProfAvatar photoUrl={row.professionalPhotoUrl} initials={row.professionalInitials} tone={received ? 'info' : 'primary'} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{labels.professional}</Typography>
              <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-bold)', lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                {row.professionalName}
              </Typography>
              {row.professionalSpecialty && (
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
                  {row.professionalSpecialty}
                </Typography>
              )}
            </Box>
          </Box>
          <DirectionTag direction={row.direction} label={row.directionLabel} />
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        {person}
        {actions}
      </Box>

      {editing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Typography variant="overline" sx={{ color: 'var(--color-primary-dark)' }}>
            Editando o que {personName} pode fazer
          </Typography>
          <PermMatrix
            entities={vm.entities}
            values={row.editPermissions}
            onChange={(key, level) => a.onChangeEditPermission?.(row.key, key, level)}
            disabled={row.saving}
            idPrefix={`edit-${row.key}`}
          />
          <Hint>{vm.grantForm?.rulesHint}</Hint>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button onClick={() => a.onCancelEdit?.()} disabled={row.saving} sx={QUIET_BTN_SX}>Cancelar</Button>
            <Button
              onClick={() => a.onSaveEdit?.(row.key)}
              disabled={row.saving}
              disableElevation
              startIcon={row.saving ? <Spinner label="Salvando" /> : <CheckRounded />}
              sx={PRIMARY_BTN_SX}
            >
              {row.saving ? 'Salvando…' : 'Salvar permissões'}
            </Button>
          </Box>
        </Box>
      ) : (
        // Removendo: o medidor continua com contraste cheio (é o que a pessoa vai perder) — o aviso vem logo abaixo.
        <PermMeter entities={vm.entities} levels={row.permissionLevels} label={`${labels.permissions} de ${who}`} />
      )}

      {/* Sem editar: diz por quê, logo abaixo do que a pessoa não pode mudar. */}
      {!row.canEdit && !editing && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: -0.5 }}>
          <LockOutlined aria-hidden="true" sx={{ fontSize: 14, color: 'var(--text-secondary)' }} />
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>Só o dono altera as permissões</Typography>
        </Box>
      )}

      {revoking && <RevokeConfirm row={row} a={a} received={received} quiet={quiet} focusFlag={focoNaConfirmacao} />}

      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', columnGap: 1, rowGap: 1, flexWrap: 'wrap',
          ...(inGroup ? {} : { pt: 1.5, borderTop: '1px solid', borderColor: 'var(--divider)' }),
        }}
      >
        <Dates row={row} labels={labels} />
        {textRevoke && (
          <Button
            ref={triggerRef}
            onClick={() => a.onAskRevoke?.(row.key)}
            startIcon={<LogoutRounded />}
            aria-label={`${revoke.actionLabel}: ${prof}`}
            sx={{ ...LEAVE_BTN_SX, minHeight: 40, ml: 'auto' }}
          >
            {revoke.actionLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
}

/* ── Grupo: um profissional meu e todas as pessoas que o veem ─────────── */
function ProfessionalGroup({ rows, vm, a, quiet }) {
  const first = rows[0];
  const name = first.professionalName;
  const specialty = first.professionalSpecialty;
  const n = rows.length;
  const busy = rows.some((r) => r.mode !== 'idle');
  return (
    <Box
      component="article"
      aria-label={`${profLabel(name, specialty)}: ${n} ${n === 1 ? 'pessoa com acesso' : 'pessoas com acesso'}`}
      sx={{ ...CARD_SX, ...(busy ? {} : HOVER_SX), p: { xs: 1, sm: 1.25 }, display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1, pt: 1, pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <ProfAvatar photoUrl={first.professionalPhotoUrl} initials={first.professionalInitials} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-bold)', lineHeight: 1.3, overflowWrap: 'anywhere' }}>
              {name}
            </Typography>
            {/* Especialidade logo abaixo do nome, como no cartão recebido. Sem ela, o cabeçalho só encolhe. */}
            {specialty && (
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
                {specialty}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <VisibilityRounded aria-hidden="true" sx={{ fontSize: 14, color: 'var(--text-secondary)' }} />
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                {n === 1 ? 'Visto por 1 pessoa' : `Visto por ${n} pessoas`}
              </Typography>
            </Box>
          </Box>
        </Box>
        <DirectionTag direction="given" label={first.directionLabel} />
      </Box>
      <Box
        component="ul"
        sx={{
          listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.5,
          '& > li + li': { position: 'relative' },
          '& > li + li::before': {
            content: '""', position: 'absolute', top: -3, left: 12, right: 12, borderTop: '1px solid var(--divider)',
          },
        }}
      >
        {rows.map((r) => <AccessItem key={r.key} row={r} vm={vm} a={a} inGroup quiet={quiet} />)}
      </Box>
    </Box>
  );
}

/* ── Faixa (Você compartilhou / Compartilharam com você) ──────────────── */
// titleRef: o título recebe o foco quando uma linha desta faixa sai com sucesso (remover/sair deu certo).
function Lane({ direction, title, description, count, children, emptyNote, titleRef }) {
  const given = direction === 'given';
  const Icon = given ? CallMadeRounded : CallReceivedRounded;
  return (
    <Box component="section" aria-label={title} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1.25, borderBottom: '2px solid', borderColor: given ? 'var(--color-primary)' : 'var(--tint-info-fg)' }}>
        <IconDot icon={<Icon sx={{ fontSize: 20 }} />} tone={given ? 'primary' : 'info'} size={36} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography ref={titleRef} tabIndex={-1} variant="h6" component="h2" sx={{ ...FOCUS_TITLE_SX, color: 'var(--text-primary)', width: 'fit-content', maxWidth: '100%' }}>{title}</Typography>
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{description}</Typography>
        </Box>
        <Box
          aria-label={`${count} ${count === 1 ? 'acesso' : 'acessos'}`}
          sx={{
            minWidth: 32, height: 28, px: 1, borderRadius: 999, flexShrink: 0,
            bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Typography variant="subtitle2" sx={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{count}</Typography>
        </Box>
      </Box>
      {count === 0 ? (
        <Box sx={{ border: '1px dashed', borderColor: 'var(--divider)', borderRadius: 'var(--radius-card)', px: 2, py: 2.5, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>{emptyNote}</Typography>
        </Box>
      ) : children}
    </Box>
  );
}

const LANES_SX = {
  display: 'grid', gap: 'var(--space-3)', gridTemplateColumns: 'minmax(0, 1fr)', alignItems: 'start',
  '@container acessos (min-width: 980px)': { gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)' },
};
// Cartões dentro de cada faixa refluem sozinhos pela largura real.
const CARDS_SX = {
  display: 'grid', gap: 'var(--space-2)', alignItems: 'start',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
};

function Lanes({ vm, a, quiet, givenTitleRef, receivedTitleRef }) {
  const rows = vm.rows || [];
  const given = rows.filter((r) => r.direction === 'given');
  const received = rows.filter((r) => r.direction !== 'given');
  // Agrupa os ENVIADOS por professionalId (ordem de chegada preservada): "quem vê a agenda da Ana?".
  // Nunca pelo nome — dois profissionais diferentes podem se chamar igual.
  const groups = [];
  const byId = {};
  given.forEach((r) => {
    if (!byId[r.professionalId]) { byId[r.professionalId] = { id: r.professionalId, rows: [] }; groups.push(byId[r.professionalId]); }
    byId[r.professionalId].rows.push(r);
  });

  return (
    <Box sx={LANES_SX}>
      <Lane
        direction="given"
        title="Você compartilhou"
        description="Seus profissionais e quem enxerga cada um"
        count={given.length}
        emptyNote="Você ainda não abriu nenhum profissional para outra conta."
        titleRef={givenTitleRef}
      >
        <Box sx={CARDS_SX}>
          {groups.map((g) => <ProfessionalGroup key={g.id} rows={g.rows} vm={vm} a={a} quiet={quiet} />)}
        </Box>
      </Lane>
      <Lane
        direction="received"
        title="Compartilharam com você"
        description="Profissionais de outras contas que você acessa"
        count={received.length}
        emptyNote="Ninguém abriu um profissional para você ainda."
        titleRef={receivedTitleRef}
      >
        <Box sx={CARDS_SX}>
          {received.map((r) => <AccessItem key={r.key} row={r} vm={vm} a={a} quiet={quiet} />)}
        </Box>
      </Lane>
    </Box>
  );
}

/* ── Formulário de compartilhar: passo a passo leve ───────────────────── */
function Step({ n, done, title, children }) {
  return (
    <Box component="li" sx={{ display: 'grid', gridTemplateColumns: '32px minmax(0, 1fr)', columnGap: 1.5, minWidth: 0 }}>
      <Box
        aria-hidden="true"
        sx={{
          width: 32, height: 32, borderRadius: '50%',
          bgcolor: done ? 'var(--color-primary)' : 'var(--color-primary-soft)',
          color: done ? 'var(--on-primary)' : 'var(--color-primary-dark)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {done ? <CheckRounded sx={{ fontSize: 18 }} /> : <Typography variant="subtitle2" sx={{ color: 'inherit' }}>{n}</Typography>}
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25, pt: '5px' }}>
        <Typography variant="subtitle2" sx={{ color: 'var(--text-primary)' }}>
          {title}
          {done && <Box component="span" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}> (concluído)</Box>}
        </Typography>
        {children}
      </Box>
    </Box>
  );
}

function SearchResult({ form, a }) {
  const r = form.searchResult;
  if (!r) return null;
  if (r.found) {
    return (
      <Box
        role="status"
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.25,
          bgcolor: 'var(--tint-success-bg)', border: '1px solid', borderColor: 'var(--tint-success-border)',
          borderRadius: 'var(--radius-sm)', p: 1.5,
        }}
      >
        <IconDot icon={<HowToRegRounded sx={{ fontSize: 22 }} />} tone="success" size={44} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'var(--tint-success-fg)', fontWeight: 'var(--fw-bold)' }}>
            Conta encontrada
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-bold)', overflowWrap: 'anywhere' }}>
            {r.userName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>{r.userEmail}</Typography>
        </Box>
      </Box>
    );
  }
  return (
    <Box
      role="status"
      sx={{
        bgcolor: 'var(--color-primary-soft)', border: '1px dashed', borderColor: 'var(--color-primary-light)',
        borderRadius: 'var(--radius-sm)', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <IconDot icon={<ForwardToInboxRounded sx={{ fontSize: 22 }} />} tone="primary" size={44} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ color: 'var(--text-primary)' }}>
            Essa pessoa ainda não está no Agendário
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
            Mande um convite para <Box component="strong" sx={{ color: 'var(--text-primary)' }}>{form.email}</Box>: ela recebe um e-mail chamando para criar a conta. Depois que ela criar, é só buscar este e-mail de novo e compartilhar.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={() => a.onSendInvite?.()} startIcon={<MailOutlineRounded />} sx={{ ...OUTLINE_BTN_SX, bgcolor: 'var(--surface)', width: { xs: '100%', sm: 'auto' } }}>
          Enviar convite
        </Button>
      </Box>
    </Box>
  );
}

function GrantPanel({ vm, a }) {
  const f = vm.grantForm || {};
  const found = !!f.searchResult?.found;
  return (
    <Box
      component="section"
      aria-labelledby="grant-title"
      sx={{
        ...CARD_SX, ...CONTAINER_SX, containerName: 'form',
        borderColor: 'var(--color-primary-light)', mb: 'var(--space-3)', overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.25 }, pb: 1.5, bgcolor: 'var(--color-primary-soft)' }}>
        <IconDot icon={<PersonAddAltRounded sx={{ fontSize: 22 }} />} tone="primary" size={40} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography id="grant-title" variant="subtitle1" component="h2" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-bold)' }}>
            Compartilhar um profissional
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.25 }}>
            Escolha o profissional, encontre a pessoa e decida o que ela pode ver ou mexer.
          </Typography>
        </Box>
        <Tooltip title="Fechar">
          <IconButton aria-label="Fechar o formulário de compartilhar" onClick={() => a.onCancelGrant?.()} sx={ICON_BTN_SX}>
            <CloseRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        component="ol"
        sx={{
          listStyle: 'none', m: 0, px: { xs: 2, sm: 2.5 }, py: 2.5,
          display: 'grid', gap: 3, gridTemplateColumns: 'minmax(0, 1fr)',
          '@container form (min-width: 860px)': {
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr)', columnGap: 4,
            '& > li:nth-of-type(3)': { gridColumn: 2, gridRow: '1 / span 2' },
          },
        }}
      >
        <Step n={1} done={!!f.selectedProfessionalId} title="Qual profissional?">
          <Select
            value={f.selectedProfessionalId || ''}
            onChange={(e) => a.onChangeGrantProfessional?.(e.target.value)}
            displayEmpty
            inputProps={{ 'aria-label': 'Profissional a compartilhar' }}
            disabled={f.granting}
            renderValue={(id) => {
              const p = (f.professionals || []).find((x) => x.id === id);
              return p
                ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <ProfAvatar key={p.id} photoUrl={p.photoUrl} initials={p.initials} size={28} />
                    <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                      {p.specialty && (
                        <Box component="span" sx={{ color: 'var(--text-secondary)' }}>{` · ${p.specialty}`}</Box>
                      )}
                    </Box>
                  </Box>
                )
                : <Box component="span" sx={{ color: 'var(--text-secondary)' }}>Escolha um profissional</Box>;
            }}
            sx={{ borderRadius: 'var(--radius-sm)', minHeight: 42, bgcolor: 'var(--surface)', '& .MuiSelect-select': { py: '7px' } }}
          >
            {(f.professionals || []).map((p) => (
              <MenuItem key={p.id} value={p.id} aria-label={profLabel(p.name, p.specialty)} sx={{ minHeight: 48, gap: 1.25 }}>
                <ProfAvatar photoUrl={p.photoUrl} initials={p.initials} size={32} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>{p.name}</Typography>
                  {p.specialty && (
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{p.specialty}</Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Step>

        <Step n={2} done={found} title="Com quem? Busque pelo e-mail">
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            <TextField
              type="email"
              value={f.email || ''}
              onChange={(e) => a.onChangeEmail?.(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); a.onSearchEmail?.(); } }}
              placeholder="nome@email.com"
              error={!!f.emailError}
              helperText={f.emailError || ' '}
              disabled={f.granting}
              inputProps={{ 'aria-label': 'E-mail da pessoa' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineRounded sx={{ fontSize: 20, color: f.emailError ? 'var(--color-error)' : 'var(--text-secondary)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: '1 1 220px', minWidth: 0,
                '& .MuiOutlinedInput-root': { borderRadius: 'var(--radius-sm)', minHeight: 42, bgcolor: 'var(--surface)' },
                '& .MuiFormHelperText-root': { mx: 0.25, fontWeight: 'var(--fw-medium)' },
                '& .MuiFormHelperText-root.Mui-error': { color: 'var(--color-error)' },
              }}
            />
            <Button
              onClick={() => a.onSearchEmail?.()}
              disabled={f.searching || f.granting}
              startIcon={f.searching ? <Spinner label="Buscando" /> : <PersonSearchRounded />}
              sx={{ ...OUTLINE_BTN_SX, width: { xs: '100%', sm: 'auto' } }}
            >
              {f.searching ? 'Buscando…' : 'Buscar'}
            </Button>
          </Box>
          <SearchResult form={f} a={a} />
        </Step>

        <Step n={3} title="O que essa pessoa pode fazer?">
          <PermMatrix
            entities={vm.entities}
            values={f.permissions}
            onChange={(key, level) => a.onChangeGrantPermission?.(key, level)}
            disabled={f.granting}
            idPrefix="grant"
          />
          <Hint>{f.rulesHint}</Hint>
        </Step>
      </Box>

      <Box
        sx={{
          display: 'flex', gap: 1, justifyContent: 'flex-end', flexDirection: { xs: 'column-reverse', sm: 'row' },
          px: { xs: 2, sm: 2.5 }, py: 1.75, borderTop: '1px solid', borderColor: 'var(--divider)', bgcolor: 'var(--bg-page)',
        }}
      >
        <Button onClick={() => a.onCancelGrant?.()} disabled={f.granting} sx={QUIET_BTN_SX}>Cancelar</Button>
        <Button
          onClick={() => a.onGrant?.()}
          disabled={!f.canGrant || f.granting}
          disableElevation
          startIcon={f.granting ? <Spinner label="Compartilhando" /> : <CheckRounded />}
          sx={PRIMARY_BTN_SX}
        >
          {f.granting ? 'Compartilhando…' : 'Compartilhar acesso'}
        </Button>
      </Box>
    </Box>
  );
}

/* ── Blocos de estado ─────────────────────────────────────────────────── */
function StateBlock({ icon, title, description, action, children }) {
  return (
    <Box sx={{ ...CARD_SX, px: 3, py: { xs: 5, sm: 7 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      {icon}
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', mb: 0.5 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 440, lineHeight: 1.5 }}>
          {description}
        </Typography>
      )}
      {children}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

function RoundIcon({ children }) {
  return (
    <Box sx={{ width: 64, height: 64, borderRadius: '50%', mb: 2, bgcolor: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </Box>
  );
}

// Vazio com personalidade: um profissional ao centro e as "pontes" possíveis para outras contas.
function EmptyIllustration() {
  const side = (icon) => (
    <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
      {icon}
    </Box>
  );
  const bridge = <Box sx={{ width: { xs: 28, sm: 48 }, borderTop: '2px dashed', borderColor: 'var(--color-primary-light)' }} />;
  return (
    <Box aria-hidden="true" sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
      {side(<StorefrontRounded sx={{ fontSize: 20 }} />)}
      {bridge}
      <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: 'var(--color-primary-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-primary-dark)', boxShadow: '0 0 0 6px var(--surface), 0 0 0 7px var(--color-primary-soft)' }}>
        <BadgeRounded sx={{ fontSize: 34 }} />
      </Box>
      {bridge}
      {side(<MedicalServicesRounded sx={{ fontSize: 20 }} />)}
    </Box>
  );
}

const EXAMPLES = [
  { Icon: StorefrontRounded, label: 'A recepção de outra unidade' },
  { Icon: HandshakeRounded, label: 'Um sócio' },
  { Icon: MedicalServicesRounded, label: 'A secretária de um consultório parceiro' },
];

function LoadingLanes() {
  const bone = { bgcolor: 'var(--divider)', transform: 'none' };
  const card = (k, lines) => (
    <Box key={k} sx={{ ...CARD_SX, p: 2.25, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Skeleton variant="circular" width={44} height={44} sx={bone} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="rounded" height={14} width="60%" sx={{ ...bone, mb: 0.75 }} />
          <Skeleton variant="rounded" height={10} width="35%" sx={bone} />
        </Box>
      </Box>
      {Array.from({ length: lines }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pt: 1, borderTop: '1px solid', borderColor: 'var(--divider)' }}>
          <Skeleton variant="circular" width={36} height={36} sx={bone} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="rounded" height={12} width="45%" sx={{ ...bone, mb: 0.75 }} />
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <Skeleton variant="rounded" height={22} width={96} sx={{ ...bone, borderRadius: 999 }} />
              <Skeleton variant="rounded" height={22} width={120} sx={{ ...bone, borderRadius: 999 }} />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
  const laneHead = (k) => (
    <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1.25, borderBottom: '2px solid', borderColor: 'var(--divider)' }}>
      <Skeleton variant="circular" width={36} height={36} sx={bone} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="rounded" height={14} width="40%" sx={{ ...bone, mb: 0.75 }} />
        <Skeleton variant="rounded" height={10} width="60%" sx={bone} />
      </Box>
    </Box>
  );
  return (
    <Box role="status" aria-live="polite" aria-busy="true">
      <Box sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>Carregando acessos…</Box>
      <Box sx={LANES_SX} aria-hidden="true">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {laneHead('g')}
          <Box sx={CARDS_SX}>{[card('a', 2), card('b', 1)]}</Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {laneHead('r')}
          <Box sx={CARDS_SX}>{card('c', 1)}</Box>
        </Box>
      </Box>
    </Box>
  );
}

/* ── View ─────────────────────────────────────────────────────────────── */
export default function AccessView({ vm, actions }) {
  const a = actions || {};
  const formOpen = !!vm.grantForm?.open;
  const st = vm.viewState;

  // Primeiro desenho da tela: uma confirmação que já nasce aberta (o mock do estúdio) não rola nem rouba foco.
  const montou = useRef(false);
  const primeiroDesenho = !montou.current;
  useEffect(() => { montou.current = true; }, []);

  // Deu certo: a linha que estava em 'revoking' some de vm.rows no mesmo instante em que a confirmação fecha.
  // O foco (que estava na caixa e caiu na página) vai para o título da faixa onde ela estava; se a lista
  // inteira acabou (não há mais faixas), para o título da página.
  const pageTitleRef = useRef(null);
  const givenTitleRef = useRef(null);
  const receivedTitleRef = useRef(null);
  const linhasAntes = useRef(vm.rows || []);
  useEffect(() => {
    const antes = linhasAntes.current;
    const agora = vm.rows || [];
    linhasAntes.current = agora;
    if (antes === agora) return;
    const ficaram = new Set(agora.map((r) => r.key));
    const saiu = antes.find((r) => r.mode === 'revoking' && !ficaram.has(r.key));
    if (!saiu || !focoPerdido()) return;
    const faixa = saiu.direction === 'given' ? givenTitleRef.current : receivedTitleRef.current;
    levarFoco(faixa || pageTitleRef.current, 'teclado');
  }, [vm.rows]);

  // Com uma confirmação aberta, a tela ganha a folga do aviso no fim: assim até o último cartão tem para onde
  // subir e termina acima da faixa do aviso de erro. Some quando a confirmação fecha.
  const confirmando = st === 'ready' && (vm.rows || []).some((r) => r.mode === 'revoking' && r.revoke);

  const toggle = (
    <Button
      onClick={() => a.onToggleGrantForm?.()}
      startIcon={formOpen ? <CloseRounded /> : <PersonAddAltRounded />}
      disableElevation
      aria-expanded={formOpen}
      sx={{ ...(formOpen ? QUIET_BTN_SX : PRIMARY_BTN_SX), width: { xs: '100%', sm: 'auto' } }}
    >
      {vm.toggleLabel}
    </Button>
  );

  let body = null;
  if (st === 'loading') {
    body = <LoadingLanes />;
  } else if (st === 'error') {
    body = (
      <StateBlock
        icon={<RoundIcon><ErrorRounded sx={{ fontSize: 32, color: 'var(--color-error)' }} /></RoundIcon>}
        title="Não foi possível carregar os acessos"
        description={vm.errorMessage}
        action={(
          <Button onClick={() => a.onRetry?.()} startIcon={<ReplayRounded />} sx={OUTLINE_BTN_SX}>
            Tentar novamente
          </Button>
        )}
      />
    );
  } else if (st === 'empty' || !(vm.rows || []).length) {
    body = (
      <StateBlock icon={<EmptyIllustration />} title="Nenhum acesso por aqui ainda" description={vm.emptyMessage}>
        <Box component="ul" aria-label="Com quem costuma fazer sentido compartilhar" sx={{ listStyle: 'none', m: 0, p: 0, mt: 2.5, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
          {EXAMPLES.map(({ Icon, label }) => (
            <Box
              component="li"
              key={label}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.75,
                bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)', borderRadius: 999, px: 1.5, py: 0.75,
              }}
            >
              <Icon aria-hidden="true" sx={{ fontSize: 16, color: 'var(--color-primary-dark)' }} />
              <Typography variant="caption" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>{label}</Typography>
            </Box>
          ))}
        </Box>
      </StateBlock>
    );
  } else {
    body = (
      <Lanes vm={vm} a={a} quiet={primeiroDesenho} givenTitleRef={givenTitleRef} receivedTitleRef={receivedTitleRef} />
    );
  }

  return (
    <Box sx={{ ...CONTAINER_SX, containerName: 'acessos', color: 'var(--text-primary)', pb: confirmando ? FOLGA_AVISO : 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 'var(--space-3)' }}>
        <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ManageAccountsRounded sx={{ fontSize: 30, color: 'var(--color-primary-dark)' }} />
            <Typography ref={pageTitleRef} tabIndex={-1} variant="h1" component="h1" sx={{ ...FOCUS_TITLE_SX, color: 'var(--text-primary)', textWrap: 'pretty' }}>
              {vm.title}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5, maxWidth: 640 }}>
            {vm.subtitle}
          </Typography>
        </Box>
        {st !== 'error' && toggle}
      </Box>

      {formOpen && st !== 'error' && <GrantPanel vm={vm} a={a} />}
      {body}
    </Box>
  );
}
